// Tutorial Editor Page - Full editing experience with sidebar, editor, and AI panel
// 3-column layout: tree sidebar | editor | AI panel (optional)
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  ChevronLeft, Save, Sparkles, Eye, EyeOff, Maximize2, Minimize2,
  Clock, Loader2, CheckCircle, AlertCircle, Download, FileDown,
  PanelLeftClose, PanelLeft, History, RotateCcw, MoreHorizontal,
  Globe, Share2, Copy, ExternalLink, ImagePlus, X, Tag,
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TUTORIAL_CATEGORIES } from './TutorialsListPage';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { tutorialAPI } from '@/lib/api';
import useTutorialStore from '@/stores/tutorialStore';
import TutorialEditor from '@/components/tutorials/TutorialEditor';
import TutorialTreeSidebar from '@/components/tutorials/TutorialTreeSidebar';
import TutorialAIPanel from '@/components/tutorials/TutorialAIPanel';
import ShareTutorialDialog from '@/components/tutorials/ShareTutorialDialog';
import { formatRelativeTime } from '@/lib/utils';
import './tutorials.css';

// Emoji picker data
const EMOJI_LIST = [
  '📘', '📗', '📕', '📙', '📒', '📓', '📔', '📚', '📖',
  '📝', '✏️', '🖊️', '💡', '⭐', '🎯', '🔧', '⚙️', '🔬',
  '🧪', '💻', '🖥️', '🌐', '🔒', '🔑', '📊', '📈', '🗂️',
  '📁', '📂', '🎓', '🎨', '🛠️', '📋', '✅', '❓', '🚀',
  '⚡', '🔥', '💎', '🏗️', '🧠', '📡', '🔗', '🎮', '📱',
];

export default function TutorialEditorPage() {
  const { pageId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    currentPage, fetchPage, fetchPages, updatePage, pages,
    saveStatus, isFullscreen, setFullscreen, setSaveStatus,
    aiPanelOpen, setAIPanelOpen, toggleStatus, toggleVisibility,
    exportHTML, exportPDF,
  } = useTutorialStore();

  const [title, setTitle] = useState('');
  const [icon, setIcon] = useState('');
  const [category, setCategory] = useState('Général');
  const [coverImage, setCoverImage] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [selectedText, setSelectedText] = useState('');

  const saveTimeoutRef = useRef(null);
  const titleRef = useRef(null);
  const editorContentRef = useRef(null);
  const coverInputRef = useRef(null);

  // Dynamic categories list combining defaults and any custom category
  const categoriesList = useMemo(() => {
    const presetNames = new Set(TUTORIAL_CATEGORIES.map(c => c.id.toLowerCase()));
    const customList = [];
    (pages || []).forEach(p => {
      if (p.category && !presetNames.has(p.category.toLowerCase())) {
        if (!customList.some(c => c.id.toLowerCase() === p.category.toLowerCase())) {
          customList.push({
            id: p.category,
            label: p.category,
            icon: '🏷️',
          });
        }
      }
    });
    if (category && !presetNames.has(category.toLowerCase()) && !customList.some(c => c.id.toLowerCase() === category.toLowerCase())) {
      customList.push({
        id: category,
        label: category,
        icon: '🏷️',
      });
    }
    return [...TUTORIAL_CATEGORIES.filter(c => c.id !== 'all'), ...customList];
  }, [pages, category]);

  // Load page and pages list
  useEffect(() => {
    if (pageId && user?.id) {
      fetchPage(pageId, user.id);
      if (pages.length === 0) {
        fetchPages(user.id);
      }
    }
  }, [pageId, user?.id]);

  // Sync title/icon/cover/category when page loads
  useEffect(() => {
    if (currentPage) {
      setTitle(currentPage.title);
      setIcon(currentPage.icon || '');
      setCategory(currentPage.category || 'Général');
      setCoverImage(currentPage.cover_image || '');
      editorContentRef.current = currentPage.content;
    }
  }, [currentPage?.id]);

  // Autosave debounce
  const triggerAutosave = useCallback((data) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    setSaveStatus('idle');
    saveTimeoutRef.current = setTimeout(() => {
      if (user?.id && pageId) {
        updatePage(pageId, data, user.id);
      }
    }, 2000);
  }, [pageId, user?.id, updatePage, setSaveStatus]);

  // Handle title change
  const handleTitleChange = (newTitle) => {
    setTitle(newTitle);
    triggerAutosave({ title: newTitle });
  };

  // Handle icon change
  const handleIconChange = (newIcon) => {
    setIcon(newIcon);
    setEmojiPickerOpen(false);
    triggerAutosave({ icon: newIcon });
  };

  // Handle category change
  const handleCategoryChange = (newCategory) => {
    if (newCategory === '__custom__') {
      const customName = prompt('Nom de la nouvelle catégorie ou thème :');
      if (customName && customName.trim()) {
        const catName = customName.trim();
        setCategory(catName);
        triggerAutosave({ category: catName });
        toast.success(`Catégorie "${catName}" appliquée`);
      }
      return;
    }
    setCategory(newCategory);
    triggerAutosave({ category: newCategory });
  };

  const handleSetCoverUrl = () => {
    const url = prompt('Collez l\'URL de l\'image de couverture (ex: https://images.unsplash.com/...) :', coverImage || '');
    if (url !== null) {
      const trimmed = url.trim();
      setCoverImage(trimmed);
      triggerAutosave({ cover_image: trimmed });
      if (trimmed) toast.success('Image de couverture mise à jour');
    }
  };

  // Handle content change from editor
  const handleContentUpdate = useCallback((json) => {
    editorContentRef.current = json;
    triggerAutosave({ content: json });
  }, [triggerAutosave]);

  // Manual save with version
  const handleSaveVersion = async () => {
    if (user?.id && pageId) {
      const data = { title, icon, content: editorContentRef.current };
      await updatePage(pageId, data, user.id, true);
      toast.success('Version sauvegardée');
    }
  };

  // Image upload handler
  const handleImageUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await tutorialAPI.uploadImage(formData, user?.id);
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${res.data.url}`;
  };

  // Cover image upload
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    try {
      const url = await handleImageUpload(file);
      setCoverImage(url);
      triggerAutosave({ cover_image: url });
      toast.success('Image de couverture ajoutée');
    } catch {
      toast.error('Erreur lors de l\'upload');
    }
    if (coverInputRef.current) coverInputRef.current.value = '';
  };

  const handleRemoveCover = () => {
    setCoverImage('');
    triggerAutosave({ cover_image: '' });
  };

  // Load versions
  const handleLoadVersions = async () => {
    if (!user?.id || !pageId) return;
    setLoadingVersions(true);
    try {
      const res = await tutorialAPI.listVersions(pageId, user.id);
      setVersions(res.data);
      setVersionsOpen(true);
    } catch {
      toast.error('Erreur lors du chargement des versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  // Restore version
  const handleRestoreVersion = async (versionId) => {
    if (!user?.id || !pageId) return;
    try {
      await tutorialAPI.restoreVersion(pageId, versionId, user.id);
      await fetchPage(pageId, user.id);
      setVersionsOpen(false);
      toast.success('Version restaurée');
    } catch {
      toast.error('Erreur lors de la restauration');
    }
  };

  // Insert AI content
  const handleInsertAIContent = (content, type) => {
    if (!editorContentRef.current) return;
    if (type === 'json' && content?.type === 'doc') {
      // Merge AI content blocks into current document
      const currentDoc = editorContentRef.current;
      const mergedContent = {
        ...currentDoc,
        content: [...(currentDoc.content || []), ...(content.content || [])],
      };
      editorContentRef.current = mergedContent;
      triggerAutosave({ content: mergedContent });
      // Force editor re-render
      fetchPage(pageId, user.id);
    } else if (type === 'text') {
      // Insert text as paragraph at the end
      const currentDoc = editorContentRef.current;
      const paragraphs = (typeof content === 'string' ? content : '').split('\n').filter(Boolean).map(line => ({
        type: 'paragraph',
        content: [{ type: 'text', text: line }],
      }));
      const mergedContent = {
        ...currentDoc,
        content: [...(currentDoc.content || []), ...paragraphs],
      };
      editorContentRef.current = mergedContent;
      triggerAutosave({ content: mergedContent });
      fetchPage(pageId, user.id);
    }
  };

  // Fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFs);
    return () => document.removeEventListener('fullscreenchange', handleFs);
  }, [setFullscreen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSaveVersion();
      }
      if (e.key === 'F11') {
        e.preventDefault();
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [title, icon]);

  if (!currentPage && !useTutorialStore.getState().loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <AlertCircle size={48} className="mx-auto text-muted-foreground/30 mb-3" />
            <h2 className="text-lg font-medium">Page non trouvée</h2>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/admin?tab=tutorials')}>
              <ChevronLeft size={16} className="mr-1" /> Retour aux tutoriels
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const saveStatusIcon = {
    idle: null,
    saving: <Loader2 size={14} className="animate-spin text-muted-foreground" />,
    saved: <CheckCircle size={14} className="text-emerald-500" />,
    error: <AlertCircle size={14} className="text-destructive" />,
  };

  const saveStatusText = {
    idle: '',
    saving: 'Enregistrement...',
    saved: 'Enregistré',
    error: 'Erreur',
  };

  return (
    <AppLayout>
      <div className={`tut-editor-page ${isFullscreen ? 'tut-editor-fullscreen' : ''}`} data-testid="tutorial-editor-page">
        {/* Top Bar */}
        <div className="tut-editor-topbar">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate('/admin?tab=tutorials')}>
              <ChevronLeft size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? <PanelLeftClose size={14} /> : <PanelLeft size={14} />}
            </Button>
            <div className="h-4 w-px bg-border" />

            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <span className="hover:text-foreground cursor-pointer" onClick={() => navigate('/admin?tab=tutorials')}>
                Tutoriels
              </span>
              <ChevronLeft size={12} className="rotate-180" />
              <span className="text-foreground font-medium truncate max-w-[200px]">
                {icon} {title}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Save status */}
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              {saveStatusIcon[saveStatus]}
              <span>{saveStatusText[saveStatus]}</span>
            </div>

            {/* Publish Button */}
            {currentPage?.status !== 'published' ? (
              <Button
                size="sm"
                className="gap-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={() => pageId && user?.id && toggleStatus(pageId, user.id)}
              >
                <Globe size={14} />
                Publier
              </Button>
            ) : (
              <Badge
                variant="default"
                className="cursor-pointer text-xs bg-emerald-600 hover:bg-emerald-700"
                onClick={() => pageId && user?.id && toggleStatus(pageId, user.id)}
              >
                <Globe size={10} className="mr-1" />
                Publié
              </Badge>
            )}

            {/* View Published */}
            {currentPage?.status === 'published' && currentPage?.is_visible && (
              <Button
                variant="outline" size="sm"
                className="gap-1.5 text-xs"
                onClick={() => window.open(`/t/${pageId}`, '_blank')}
                title="Voir le tutoriel publié"
              >
                <ExternalLink size={14} />
                <span className="hidden sm:inline">Voir</span>
              </Button>
            )}

            {/* Share */}
            <Button
              variant="outline" size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setShareDialogOpen(true)}
              title="Partager le tutoriel"
            >
              <Share2 size={14} />
              <span className="hidden sm:inline">Partager</span>
            </Button>

            {/* Visibility */}
            <Button
              variant="ghost" size="icon" className="h-8 w-8"
              onClick={() => pageId && user?.id && toggleVisibility(pageId, user.id)}
              title={currentPage?.is_visible ? 'Masquer aux candidats' : 'Rendre visible aux candidats'}
            >
              {currentPage?.is_visible ? <Eye size={14} className="text-emerald-500" /> : <EyeOff size={14} />}
            </Button>

            <div className="h-4 w-px bg-border" />

            {/* AI Panel Toggle */}
            <Button
              variant={aiPanelOpen ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setAIPanelOpen(!aiPanelOpen)}
              className="gap-1.5 text-xs"
            >
              <Sparkles size={14} />
              <span className="hidden sm:inline">IA</span>
            </Button>

            {/* More actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal size={14} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuItem onClick={handleSaveVersion}>
                  <Save size={14} className="mr-2" /> Sauvegarder une version
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLoadVersions}>
                  <History size={14} className="mr-2" /> Historique des versions
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => pageId && user?.id && exportHTML(pageId, user.id)}>
                  <Download size={14} className="mr-2" /> Exporter en HTML
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => pageId && user?.id && exportPDF(pageId, user.id)}>
                  <FileDown size={14} className="mr-2" /> Exporter en PDF
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={toggleFullscreen}>
                  {isFullscreen ? <Minimize2 size={14} className="mr-2" /> : <Maximize2 size={14} className="mr-2" />}
                  {isFullscreen ? 'Quitter le plein écran' : 'Plein écran'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="tut-editor-layout">
          {/* Tree Sidebar */}
          {sidebarOpen && (
            <div className="tut-editor-sidebar">
              <TutorialTreeSidebar
                userId={user?.id}
                currentPageId={pageId}
              />
            </div>
          )}

          {/* Editor */}
          <div className="tut-editor-main">
            <div className="tut-editor-scroll">
              {/* Cover Image */}
              {coverImage ? (
                <div className="tut-cover-image-wrapper">
                  <img src={coverImage} alt="Couverture" className="tut-cover-image" />
                  <div className="tut-cover-image-actions">
                    <button
                      type="button"
                      className="tut-cover-action-btn"
                      onClick={() => coverInputRef.current?.click()}
                      title="Changer l'image"
                    >
                      <ImagePlus size={14} />
                      Changer
                    </button>
                    <button
                      type="button"
                      className="tut-cover-action-btn"
                      onClick={handleSetCoverUrl}
                      title="Saisir une URL"
                    >
                      <Globe size={14} />
                      URL
                    </button>
                    <button
                      type="button"
                      className="tut-cover-action-btn tut-cover-action-remove"
                      onClick={handleRemoveCover}
                      title="Supprimer l'image"
                    >
                      <X size={14} />
                      Supprimer
                    </button>
                  </div>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleCoverUpload}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 py-3 border-b border-dashed border-border/60">
                  <button
                    type="button"
                    className="tut-cover-add-btn border-none"
                    onClick={() => coverInputRef.current?.click()}
                  >
                    <ImagePlus size={16} />
                    Téléverser une couverture
                    <input
                      ref={coverInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleCoverUpload}
                    />
                  </button>
                  <span className="text-xs text-muted-foreground">•</span>
                  <button
                    type="button"
                    className="tut-cover-add-btn border-none text-xs"
                    onClick={handleSetCoverUrl}
                  >
                    <Globe size={14} />
                    Coller une URL
                  </button>
                </div>
              )}

              {/* Page Header */}
              <div className="tut-page-header">
                {/* Icon & Category */}
                <div className="flex items-center justify-between gap-3 mb-2">
                  <button
                    type="button"
                    className="tut-page-icon-btn"
                    onClick={() => setEmojiPickerOpen(true)}
                    title="Changer l'icône"
                  >
                    {icon || '📄'}
                  </button>

                  <Select value={category} onValueChange={handleCategoryChange}>
                    <SelectTrigger className="w-48 h-8 text-xs font-medium border-indigo-500/20 bg-indigo-500/5 hover:bg-indigo-500/10">
                      <Tag size={12} className="mr-1.5 text-indigo-500 flex-shrink-0" />
                      <SelectValue placeholder="Thème" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoriesList.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          <span className="mr-1.5">{cat.icon}</span> {cat.label}
                        </SelectItem>
                      ))}
                      <SelectItem value="__custom__" className="text-indigo-600 font-semibold border-t mt-1 pt-1">
                        <span className="mr-1.5">➕</span> + Autre catégorie...
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Title */}
                <textarea
                  ref={titleRef}
                  value={title}
                  onChange={(e) => {
                    handleTitleChange(e.target.value);
                    // Auto-resize
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  onKeyDown={(e) => {
                    // Allow Enter for new line (no submit)
                    if (e.key === 'Enter' && !e.shiftKey) {
                      // Let it create new line naturally
                    }
                  }}
                  rows={1}
                  className="tut-page-title-input"
                  placeholder="Titre de la page..."
                  data-testid="tutorial-title-input"
                />

                {/* Meta */}
                <div className="tut-page-meta">
                  {currentPage?.author_name && (
                    <span>Par {currentPage.author_name}</span>
                  )}
                  {currentPage?.updated_at && (
                    <>
                      <span>•</span>
                      <span>Modifié {formatRelativeTime ? formatRelativeTime(currentPage.updated_at) : currentPage.updated_at?.slice(0, 10)}</span>
                    </>
                  )}
                  {currentPage?.word_count > 0 && (
                    <>
                      <span>•</span>
                      <span>{currentPage.word_count} mots</span>
                    </>
                  )}
                </div>
              </div>

              {/* Editor */}
              <TutorialEditor
                content={currentPage?.content}
                onUpdate={handleContentUpdate}
                onImageUpload={handleImageUpload}
                editable={true}
              />
            </div>
          </div>

          {/* AI Panel */}
          {aiPanelOpen && (
            <div className="tut-editor-ai-col">
              <TutorialAIPanel
                userId={user?.id}
                onInsertContent={handleInsertAIContent}
                selectedText={selectedText}
              />
            </div>
          )}
        </div>

        {/* Emoji Picker Dialog */}
        <Dialog open={emojiPickerOpen} onOpenChange={setEmojiPickerOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Choisir une icône</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-8 gap-1.5 py-2">
              {EMOJI_LIST.map(emoji => (
                <button
                  key={emoji}
                  type="button"
                  className="h-10 w-10 text-xl rounded-md hover:bg-muted flex items-center justify-center transition-colors"
                  onClick={() => handleIconChange(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={() => handleIconChange('')}>
              Supprimer l'icône
            </Button>
          </DialogContent>
        </Dialog>

        {/* Versions Dialog */}
        <Dialog open={versionsOpen} onOpenChange={setVersionsOpen}>
          <DialogContent className="sm:max-w-lg max-h-[70vh] overflow-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History size={18} /> Historique des versions
              </DialogTitle>
            </DialogHeader>
            {loadingVersions ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin" size={24} />
              </div>
            ) : versions.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucune version sauvegardée</p>
                <p className="text-xs mt-1">Utilisez Ctrl+S pour sauvegarder des versions</p>
              </div>
            ) : (
              <div className="space-y-2">
                {versions.map(v => (
                  <div key={v.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">v{v.version_number}</Badge>
                        <span className="text-sm font-medium">{v.title}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock size={10} />
                        {v.created_at?.slice(0, 16).replace('T', ' ')}
                        <span>•</span>
                        <span>{v.word_count} mots</span>
                        <span>•</span>
                        <span>{v.author_name}</span>
                      </div>
                    </div>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleRestoreVersion(v.id)}
                      className="text-xs"
                    >
                      <RotateCcw size={12} className="mr-1" /> Restaurer
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Share Dialog */}
        <ShareTutorialDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          page={currentPage}
          userId={user?.id}
          onUpdate={() => fetchPage(pageId, user?.id)}
        />
      </div>
    </AppLayout>
  );
}
